import { useId, useRef, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useModalDialog } from '@/components/ui/use-modal-dialog';

interface ModalShellProps {
  open: boolean;
  title: string;
  /** Operación en curso: bloquea el cierre por fondo y Escape. */
  busy?: boolean;
  /** Panel ancho para formularios de varios campos. */
  wide?: boolean;
  /** Id del elemento que describe el diálogo (aria-describedby), p. ej. el mensaje. */
  describedBy?: string;
  onClose: () => void;
  /** Acciones del pie (Cancelar / Confirmar). */
  footer: ReactNode;
  children: ReactNode;
}

/**
 * Armazón de un diálogo modal de formulario (UX-COMP-01, UX-A11Y-01): portal a
 * <body>, fondo que cierra (salvo `busy`), role=dialog nombrado por su título, focus
 * trap y Escape vía useModalDialog. El contenido y las acciones los pone el llamador.
 */
export function ModalShell({
  open,
  title,
  busy = false,
  wide = false,
  describedBy,
  onClose,
  footer,
  children,
}: ModalShellProps): ReactElement | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useModalDialog(open, panelRef, { onClose, busy });

  if (!open) {
    return null;
  }
  // Portal: relativo al viewport, no a una tarjeta con transform (.rise).
  return createPortal(
    <>
      <div className="modal-overlay" aria-hidden="true" onClick={() => !busy && onClose()} />
      <div
        ref={panelRef}
        className={`modal-panel${wide ? ' modal-wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
      >
        <h2 id={titleId} className="modal-title">
          {title}
        </h2>
        {children}
        <div className="modal-actions">{footer}</div>
      </div>
    </>,
    document.body,
  );
}
